import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface InductionData {
  id: string;
  site_id: string;
  worker_id: string;
  worker?: {
    name: string;
    role: string;
    email: string;
    phone?: string;
    certifications?: string[];
    whitecard_number?: string;
  };
  site?: {
    id: string;
    name: string;
    type: string;
    address: string;
    requirements?: string[];
    description?: string;
    asbestos_type?: string;
  };
  status: string;
  completed_at?: string;
  created_at: string;
  signature?: string;
}

export const generateInductionPDF = async (induction: InductionData): Promise<void> => {
  // Debug logging to check what data we're receiving
  console.log('PDF Generation - Induction data:', {
    id: induction.id,
    site: induction.site,
    worker: induction.worker,
    status: induction.status
  });
  
  if (!induction.site) {
    console.error('No site data available for PDF generation');
    alert('Cannot generate PDF: Site information is missing');
    return;
  }
  
  if (!induction.worker) {
    console.error('No worker data available for PDF generation');
    alert('Cannot generate PDF: Worker information is missing');
    return;
  }

  // Create a temporary container for the PDF content
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = '794px'; // A4 width in pixels at 96 DPI
  container.style.backgroundColor = 'white';
  container.style.padding = '30px';
  container.style.fontFamily = 'Arial, sans-serif';
  container.style.lineHeight = '1.3';
  container.style.fontSize = '12px';
  
  // Create the HTML content optimized for single page
  container.innerHTML = `
    <div style="max-width: 734px; margin: 0 auto;">
      <!-- Header -->
      <div style="text-align: center; margin-bottom: 20px; border-bottom: 2px solid #ea580c; padding-bottom: 15px;">
        <div style="display: flex; align-items: center; justify-content: center; margin-bottom: 10px;">
          <div style="width: 40px; height: 40px; background-color: #fed7aa; border-radius: 8px; display: flex; align-items: center; justify-content: center; margin-right: 12px;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ea580c" stroke-width="2">
              <path d="M9 12l2 2 4-4"/>
              <path d="M21 12c.552 0 1-.448 1-1V5c0-.552-.448-1-1-1H3c-.552 0-1 .448-1 1v6c0 .552.448 1 1 1"/>
              <path d="M3 12v6c0 .552.448 1 1 1h16c.552 0 1-.448 1-1v-6"/>
            </svg>
          </div>
          <div>
            <h1 style="font-size: 22px; font-weight: bold; color: #1f2937; margin: 0; line-height: 1.1;">SAFETY INDUCTION CERTIFICATE</h1>
            <p style="font-size: 12px; color: #6b7280; margin: 3px 0 0 0;">Demolition & Asbestos Safety Management</p>
          </div>
        </div>
      </div>

      <!-- Status Banner -->
      <div style="background-color: ${induction.status === 'completed' ? '#dcfce7' : '#fef3c7'}; 
                  border: 1px solid ${induction.status === 'completed' ? '#16a34a' : '#f59e0b'}; 
                  border-radius: 6px; padding: 10px; margin-bottom: 18px; text-align: center;">
        <span style="font-weight: 600; color: ${induction.status === 'completed' ? '#14532d' : '#92400e'}; font-size: 14px;">
          ✓ Induction ${induction.status.charAt(0).toUpperCase() + induction.status.slice(1)}
          ${induction.status === 'completed' && induction.completed_at ? 
            ` - ${new Date(induction.completed_at).toLocaleDateString()}` : ''
          }
        </span>
      </div>

      <!-- Main Content Grid -->
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 18px;">
        
        <!-- Worker Information -->
        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 15px;">
          <h3 style="font-size: 14px; font-weight: 600; color: #1f2937; margin: 0 0 10px 0; display: flex; align-items: center;">
            <span style="margin-right: 6px;">👤</span> Worker Information
          </h3>
          <div style="space-y: 8px;">
            <div style="margin-bottom: 8px;">
              <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Name</label>
              <p style="font-size: 12px; color: #1f2937; margin: 0; font-weight: 500;">${induction.worker?.name || 'N/A'}</p>
            </div>
            <div style="margin-bottom: 8px;">
              <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Role</label>
              <p style="font-size: 12px; color: #1f2937; margin: 0; text-transform: capitalize;">${induction.worker?.role?.replace('_', ' ') || 'N/A'}</p>
            </div>
            <div style="margin-bottom: 8px;">
              <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Email</label>
              <p style="font-size: 11px; color: #1f2937; margin: 0;">${induction.worker?.email || 'N/A'}</p>
            </div>
            ${induction.worker?.phone ? `
              <div style="margin-bottom: 8px;">
                <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Phone</label>
                <p style="font-size: 11px; color: #1f2937; margin: 0;">${induction.worker.phone}</p>
              </div>
            ` : ''}
            ${induction.worker?.whitecard_number ? `
              <div style="margin-bottom: 8px;">
                <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">White Card Number</label>
                <p style="font-size: 11px; color: #1f2937; margin: 0;">${induction.worker.whitecard_number}</p>
              </div>
            ` : ''}
            ${induction.worker?.start_date ? `
              <div style="margin-bottom: 8px;">
                <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Start Date</label>
                <p style="font-size: 11px; color: #1f2937; margin: 0;">${new Date(induction.worker.start_date).toLocaleDateString()}</p>
              </div>
            ` : ''}
            <div style="margin-bottom: 8px;">
              <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Worker Status</label>
              <span style="font-size: 9px; text-transform: capitalize;">${induction.worker?.status || 'Unknown'}</span>
            </div>
            ${induction.worker?.certifications && induction.worker.certifications.length > 0 ? `
              <div>
                <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 4px;">Certifications</label>
                <p style="font-size: 9px; color: #1f2937; margin: 0; line-height: 1.4;">
                  ${induction.worker.certifications.join(' - ')}
                </p>
              </div>
            ` : ''}
          </div>
        </div>

        <!-- Site Information -->
        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 15px;">
          <h3 style="font-size: 14px; font-weight: 600; color: #1f2937; margin: 0 0 10px 0; display: flex; align-items: center;">
            <span style="margin-right: 6px;">🏗️</span> Site Information
          </h3>
          <div>
            <div style="margin-bottom: 8px;">
              <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Site Name</label>
              <p style="font-size: 12px; color: #1f2937; margin: 0; font-weight: 500;">${induction.site?.name || 'N/A'}</p>
            </div>
            <div style="margin-bottom: 8px;">
              <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Type</label>
              <p style="font-size: 12px; color: #1f2937; margin: 0; text-transform: capitalize;">${induction.site?.type?.replace('_', ' ') || 'N/A'}</p>
            </div>
            <div style="margin-bottom: 8px;">
              <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Address</label>
              <p style="font-size: 11px; color: #1f2937; margin: 0; line-height: 1.3;">${induction.site?.address || 'N/A'}</p>
            </div>
            <div style="margin-bottom: 8px;">
              <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Project Duration</label>
              <p style="font-size: 10px; color: #1f2937; margin: 0;">
                ${induction.site?.start_date ? new Date(induction.site.start_date).toLocaleDateString() : 'N/A'} - 
                ${induction.site?.end_date ? new Date(induction.site.end_date).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            <div style="margin-bottom: 8px;">
              <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Site Status</label>
              <span style="font-size: 9px; text-transform: capitalize;">${induction.site?.status || 'Unknown'}</span>
            </div>
            ${induction.site?.asbestos_type && induction.site.asbestos_type !== 'none' && induction.site.asbestos_type !== '' ? `
              <div style="margin-bottom: 8px;">
                <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Asbestos Type</label>
                <p style="font-size: 11px; margin: 0; text-transform: capitalize;">${induction.site.asbestos_type.replace('_', ' ')}</p>
              </div>
            ` : ''}
            ${induction.site?.description ? `
              <div style="margin-bottom: 8px;">
                <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Site Description</label>
                <p style="font-size: 10px; color: #1f2937; margin: 0; line-height: 1.3; background-color: #f9fafb; padding: 4px 6px; border-radius: 3px; border-left: 3px solid #3b82f6;">${induction.site.description}</p>
              </div>
            ` : ''}
            ${induction.site?.photo_url ? `
              <div style="margin-bottom: 8px;">
                <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Site Photo</label>
                <p style="font-size: 9px; color: #6b7280; margin: 0; word-break: break-all;">Available: ${induction.site.photo_url.substring(0, 50)}${induction.site.photo_url.length > 50 ? '...' : ''}</p>
              </div>
            ` : ''}
            ${induction.site?.requirements && induction.site.requirements.length > 0 ? `
              <div>
                <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 4px;">Key Requirements</label>
                <p style="font-size: 9px; color: #1f2937; margin: 0; line-height: 1.4;">
                  ${induction.site.requirements.join(' - ')}
                </p>
              </div>
            ` : ''}
          </div>
        </div>
      </div>

      <!-- Induction Details & Signature Row -->
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 18px;">
        
        <!-- Induction Details -->
        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 15px;">
          <h3 style="font-size: 14px; font-weight: 600; color: #1f2937; margin: 0 0 10px 0;">Induction Details</h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
            <div>
              <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Status</label>
              <span style="font-size: 10px; font-weight: 600;">
                ${induction.status.charAt(0).toUpperCase() + induction.status.slice(1)}
              </span>
            </div>
            <div>
              <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Created</label>
              <p style="font-size: 11px; color: #1f2937; margin: 0;">${new Date(induction.created_at).toLocaleDateString()}</p>
            </div>
            ${induction.completed_at ? `
              <div style="grid-column: 1 / -1;">
                <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Completed</label>
                <p style="font-size: 11px; color: #1f2937; margin: 0;">${new Date(induction.completed_at).toLocaleDateString()}</p>
              </div>
            ` : ''}
          </div>
          <div style="margin-top: 10px;">
            <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Certificate ID</label>
            <p style="font-size: 9px; color: #1f2937; margin: 0; font-family: monospace; word-break: break-all; line-height: 1.2;">${induction.id}</p>
          </div>
        </div>

        <!-- Digital Signature -->
        ${induction.signature ? `
          <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 15px;">
            <h3 style="font-size: 14px; font-weight: 600; color: #1f2937; margin: 0 0 10px 0;">Digital Signature</h3>
            <div style="background-color: #f1f5f9; border-radius: 6px; padding: 10px; border: 1px dashed #cbd5e1;">
              <div style="background-color: white; border-radius: 4px; padding: 8px; min-height: 50px; border: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: center;">
                ${induction.signature.startsWith('data:image') ? 
                  `<img src="${induction.signature}" alt="Worker signature" style="max-width: 100%; max-height: 50px;" />` :
                  `<p style="font-size: 14px; color: #1f2937; margin: 0; font-family: 'Brush Script MT', cursive, serif; font-style: italic;">${induction.signature}</p>`
                }
              </div>
              <p style="font-size: 9px; color: #6b7280; margin: 6px 0 0 0; text-align: center;">
                Signed: ${induction.completed_at ? new Date(induction.completed_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
        ` : `
          <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 15px;">
            <h3 style="font-size: 14px; font-weight: 600; color: #1f2937; margin: 0 0 10px 0;">Digital Signature</h3>
            <div style="background-color: #f9fafb; border-radius: 6px; padding: 20px; text-align: center; border: 1px dashed #d1d5db;">
              <p style="font-size: 11px; color: #6b7280; margin: 0;">Signature pending completion</p>
            </div>
          </div>
        `}
      </div>

      <!-- Safety Checklist -->
      <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 15px; margin-bottom: 18px;">
        <h3 style="font-size: 14px; font-weight: 600; color: #1f2937; margin: 0 0 10px 0;">Safety Checklist Completed</h3>
        <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px;">
          <div style="display: flex; align-items: center;">
            <span style="color: #16a34a; margin-right: 6px; font-size: 12px;">✓</span>
            <span style="font-size: 11px; color: #374151;">Site hazards explained</span>
          </div>
          <div style="display: flex; align-items: center;">
            <span style="color: #16a34a; margin-right: 6px; font-size: 12px;">✓</span>
            <span style="font-size: 11px; color: #374151;">Emergency procedures</span>
          </div>
          <div style="display: flex; align-items: center;">
            <span style="color: #16a34a; margin-right: 6px; font-size: 12px;">✓</span>
            <span style="font-size: 11px; color: #374151;">PPE requirements</span>
          </div>
          <div style="display: flex; align-items: center;">
            <span style="color: #16a34a; margin-right: 6px; font-size: 12px;">✓</span>
            <span style="font-size: 11px; color: #374151;">Work methods</span>
          </div>
          <div style="display: flex; align-items: center;">
            <span style="color: #16a34a; margin-right: 6px; font-size: 12px;">✓</span>
            <span style="font-size: 11px; color: #374151;">Safety protocols</span>
          </div>
          <div style="display: flex; align-items: center;">
            <span style="color: #16a34a; margin-right: 6px; font-size: 12px;">✓</span>
            <span style="font-size: 11px; color: #374151;">Risk assessments</span>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div style="text-align: center; margin-top: 20px; padding-top: 15px; border-top: 1px solid #e5e7eb;">
        <p style="font-size: 10px; color: #6b7280; margin: 0;">
          This certificate confirms completion of site-specific safety induction training.
        </p>
        <p style="font-size: 9px; color: #9ca3af; margin: 4px 0 0 0;">
          Generated on ${new Date().toLocaleDateString()} • Certificate ID: ${induction.id.slice(0, 8)}
        </p>
      </div>
    </div>
  `;

  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    });

    const imgData = canvas.toDataURL('image/jpeg', 1.0);
    const pdf = new jsPDF('p', 'px', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
    const imgX = (pdfWidth - imgWidth * ratio) / 2;
    const imgY = 0;

    pdf.addImage(imgData, 'JPEG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
    pdf.save(`induction-certificate-${induction.id.slice(0, 8)}.pdf`);
  } finally {
    document.body.removeChild(container);
  }
};